import { signInWithCustomToken, signOut } from "firebase/auth";
import { JUNGLE_CLOUDFLARE_BASE_URL } from "../../constants/GlobalTypesAndEnumsAndConstants";
import { auth } from "./init-firebase";
import {
  markFirebaseSessionAsExpected,
  markFirebaseSessionEndAsExpected,
} from "./auth-session-observability";

export const MOBILE_AUTHENTICATION_BRIDGE_VERSION = 2;

const MAX_FIREBASE_USER_ID_LENGTH = 128;
const MAX_FIREBASE_ID_TOKEN_LENGTH = 8192;
const MAX_WEB_CUSTOM_TOKEN_LENGTH = 8192;
const MAX_MOBILE_VERSION_FIELD_LENGTH = 64;

const KNOWN_FIREBASE_AUTHENTICATION_ERROR_CODES = [
  "auth/app-not-authorized",
  "auth/custom-token-mismatch",
  "auth/internal-error",
  "auth/invalid-api-key",
  "auth/invalid-custom-token",
  "auth/invalid-user-token",
  "auth/network-request-failed",
  "auth/operation-not-allowed",
  "auth/too-many-requests",
  "auth/user-disabled",
  "auth/user-token-expired",
  "auth/web-storage-unsupported",
] as const;

type KnownFirebaseAuthenticationErrorCode = (typeof KNOWN_FIREBASE_AUTHENTICATION_ERROR_CODES)[number];
export type MobileAuthenticationFirebaseErrorCode = KnownFirebaseAuthenticationErrorCode | "unknown";
type MobileAuthenticationRetryFailureStage = "token_exchange" | "firebase_sign_in";

type SecureMobileAuthenticationPayload = {
  bridgeVersion: typeof MOBILE_AUTHENTICATION_BRIDGE_VERSION;
  userId: string;
  firebaseIdToken: string;
  /** Optional: native already exchanged the ID token (avoids WebView CORS). */
  webCustomToken?: string;
  nativeAppVersion?: string;
  nativeBuildNumber?: string;
};

type ParsedMobileAuthenticationMessage =
  | {
      kind: "secure";
      payload: SecureMobileAuthenticationPayload;
    }
  | {
      kind: "legacy";
      userId: string;
    };

function getSecureMobileAuthenticationSessionIdentifier(payload: SecureMobileAuthenticationPayload): string {
  // This unverified claim only correlates a session the server has rejected;
  // it never establishes authentication. Token refresh preserves auth_time,
  // while a fresh sign-in for the same account establishes a different session.
  try {
    const parts = payload.firebaseIdToken.split(".");
    if (parts.length === 3) {
      const encodedClaims = parts[1].replace(/-/g, "+").replace(/_/g, "/");
      const claims = JSON.parse(atob(encodedClaims.padEnd(Math.ceil(encodedClaims.length / 4) * 4, "=")));
      if (typeof claims?.auth_time === "number" && Number.isSafeInteger(claims.auth_time) && claims.auth_time >= 0) {
        return JSON.stringify([payload.userId, "auth_time", claims.auth_time]);
      }
    }
  } catch { /* Opaque or malformed tokens can still be correlated exactly. */ }
  return JSON.stringify([payload.userId, "token", payload.firebaseIdToken]);
}

export function getMobileAuthenticationSessionIdentifier(rawMessage: unknown): string | null {
  try {
    const parsedMessage = parseMobileAuthenticationMessage(rawMessage);
    return parsedMessage.kind === "secure" ? getSecureMobileAuthenticationSessionIdentifier(parsedMessage.payload) : null;
  } catch {
    // The normal authentication path retains payload validation/reporting.
    return null;
  }
}

const authenticationRequestsInFlight = new Map<string, {
  generation: number;
  promise: Promise<MobileAuthenticationResult>;
}>();
let authenticationRequestGeneration = 0;
// Firebase auth mutations cannot be cancelled once started. Serializing them
// ensures the newest native authentication event determines the final session.
let firebaseAuthenticationMutationQueue: Promise<void> = Promise.resolve();
let signOutRequestInFlight: Promise<void> | null = null;

export type MobileAuthenticationResult =
  | {
      status: "signed_in";
      bridgeVersion: typeof MOBILE_AUTHENTICATION_BRIDGE_VERSION;
      userId: string;
      nativeAppVersion?: string;
      nativeBuildNumber?: string;
      firebaseRetryAttempted?: boolean;
      initialFirebaseErrorCode?: MobileAuthenticationFirebaseErrorCode;
    }
  | {
      status: "already_signed_in";
      bridgeVersion: typeof MOBILE_AUTHENTICATION_BRIDGE_VERSION | "legacy";
      userId: string;
      nativeAppVersion?: string;
      nativeBuildNumber?: string;
    }
  | {
      status: "legacy_bridge_rejected";
      bridgeVersion: "legacy";
      userId: string;
    }
  | {
      status: "authentication_superseded" | "reauthentication_required";
      bridgeVersion: typeof MOBILE_AUTHENTICATION_BRIDGE_VERSION | "legacy";
      userId: string;
      nativeAppVersion?: string;
      nativeBuildNumber?: string;
    };

export type MobileAuthenticationErrorCode =
  | "invalid_payload"
  | "unsupported_bridge_version"
  | "token_exchange_failed"
  | "session_reauthentication_required"
  | "firebase_sign_in_failed"
  | "firebase_sign_out_failed";

export class MobileAuthenticationError extends Error {
  readonly code: MobileAuthenticationErrorCode;
  readonly bridgeVersion: string;
  readonly userId?: string;
  readonly nativeAppVersion?: string;
  readonly nativeBuildNumber?: string;
  readonly responseStatus?: number;
  readonly firebaseErrorCode?: MobileAuthenticationFirebaseErrorCode;
  readonly initialFirebaseErrorCode?: MobileAuthenticationFirebaseErrorCode;
  readonly firebaseRetryAttempted?: boolean;
  readonly firebaseRetryFailureStage?: MobileAuthenticationRetryFailureStage;

  constructor({
    code,
    message,
    bridgeVersion,
    userId,
    nativeAppVersion,
    nativeBuildNumber,
    responseStatus,
    firebaseErrorCode,
    initialFirebaseErrorCode,
    firebaseRetryAttempted,
    firebaseRetryFailureStage,
  }: {
    code: MobileAuthenticationErrorCode;
    message: string;
    bridgeVersion: string;
    userId?: string;
    nativeAppVersion?: string;
    nativeBuildNumber?: string;
    responseStatus?: number;
    firebaseErrorCode?: MobileAuthenticationFirebaseErrorCode;
    initialFirebaseErrorCode?: MobileAuthenticationFirebaseErrorCode;
    firebaseRetryAttempted?: boolean;
    firebaseRetryFailureStage?: MobileAuthenticationRetryFailureStage;
  }) {
    super(message);
    this.name = "MobileAuthenticationError";
    this.code = code;
    this.bridgeVersion = bridgeVersion;
    this.userId = userId;
    this.nativeAppVersion = nativeAppVersion;
    this.nativeBuildNumber = nativeBuildNumber;
    this.responseStatus = responseStatus;
    this.firebaseErrorCode = firebaseErrorCode;
    this.initialFirebaseErrorCode = initialFirebaseErrorCode;
    this.firebaseRetryAttempted = firebaseRetryAttempted;
    this.firebaseRetryFailureStage = firebaseRetryFailureStage;
  }
}

function normalizeRequiredString(value: unknown, maxLength: number): string | null {
  if (typeof value !== "string") return null;
  const normalizedValue = value.trim();
  if (!normalizedValue || normalizedValue.length > maxLength) return null;
  return normalizedValue;
}

function normalizeOptionalVersionField(value: unknown): string | undefined {
  return normalizeRequiredString(value, MAX_MOBILE_VERSION_FIELD_LENGTH) ?? undefined;
}

function getFirebaseAuthenticationErrorCode(error: unknown): MobileAuthenticationFirebaseErrorCode {
  const untrustedErrorCode =
    typeof error === "object" && error !== null && "code" in error ? (error as { code?: unknown }).code : undefined;
  if (typeof untrustedErrorCode !== "string") return "unknown";

  return KNOWN_FIREBASE_AUTHENTICATION_ERROR_CODES.includes(untrustedErrorCode as KnownFirebaseAuthenticationErrorCode)
    ? (untrustedErrorCode as KnownFirebaseAuthenticationErrorCode)
    : "unknown";
}

function isRetryableFirebaseAuthenticationErrorCode(firebaseErrorCode: MobileAuthenticationFirebaseErrorCode): boolean {
  return firebaseErrorCode === "auth/network-request-failed" || firebaseErrorCode === "auth/internal-error";
}

async function exchangeFirebaseIdTokenForWebCustomToken({
  userId,
  firebaseIdToken,
  nativeAppVersion,
  nativeBuildNumber,
  noteSessionRejected,
}: {
  userId: string;
  firebaseIdToken: string;
  nativeAppVersion?: string;
  nativeBuildNumber?: string;
  noteSessionRejected?: () => void;
}): Promise<string> {
  const customTokenResponse = await fetch(`${JUNGLE_CLOUDFLARE_BASE_URL}/users/get_firebase_custom_token`, {
    method: "POST",
    body: JSON.stringify({ user_id: userId }),
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${firebaseIdToken}`,
    },
  }).catch(() => null);

  if (!customTokenResponse?.ok || customTokenResponse.status !== 200) {
    // An ID-token refresh cannot repair a containment-cutoff rejection: it
    // preserves auth_time. Only this explicit server verdict ends native auth.
    const rejection = customTokenResponse?.status === 401
      ? await customTokenResponse.json().catch(() => null)
      : null;
    const needsFreshSignIn = rejection?.error === "invalid_auth_token" && rejection?.reauthentication_required === true;
    // Remember the server verdict even if another handoff superseded this
    // request. Recording a rejected session must not mutate either login.
    if (needsFreshSignIn) noteSessionRejected?.();
    throw new MobileAuthenticationError({
      code: needsFreshSignIn ? "session_reauthentication_required" : "token_exchange_failed",
      message: "Secure mobile authentication token exchange failed",
      bridgeVersion: String(MOBILE_AUTHENTICATION_BRIDGE_VERSION),
      userId,
      nativeAppVersion,
      nativeBuildNumber,
      responseStatus: customTokenResponse?.status,
    });
  }

  const customToken = normalizeRequiredString(await customTokenResponse.text(), MAX_WEB_CUSTOM_TOKEN_LENGTH);
  if (!customToken) {
    throw new MobileAuthenticationError({
      code: "token_exchange_failed",
      message: "Secure mobile authentication token exchange returned an empty token",
      bridgeVersion: String(MOBILE_AUTHENTICATION_BRIDGE_VERSION),
      userId,
      nativeAppVersion,
      nativeBuildNumber,
      responseStatus: customTokenResponse.status,
    });
  }

  return customToken;
}

function queueFirebaseAuthenticationMutation<T>(mutation: () => Promise<T>): Promise<T> {
  const queuedMutation = firebaseAuthenticationMutationQueue.then(mutation, mutation);
  firebaseAuthenticationMutationQueue = queuedMutation.then(
    () => undefined,
    () => undefined
  );
  return queuedMutation;
}

function createSupersededResult(
  parsedMessage: ParsedMobileAuthenticationMessage,
  userId: string
): MobileAuthenticationResult {
  return {
    status: "authentication_superseded",
    bridgeVersion: parsedMessage.kind === "secure" ? MOBILE_AUTHENTICATION_BRIDGE_VERSION : "legacy",
    userId,
    nativeAppVersion: parsedMessage.kind === "secure" ? parsedMessage.payload.nativeAppVersion : undefined,
    nativeBuildNumber: parsedMessage.kind === "secure" ? parsedMessage.payload.nativeBuildNumber : undefined,
  };
}

export function parseMobileAuthenticationMessage(rawMessage: unknown): ParsedMobileAuthenticationMessage {
  const normalizedMessage = normalizeRequiredString(rawMessage, MAX_FIREBASE_ID_TOKEN_LENGTH * 2);
  if (!normalizedMessage) {
    throw new MobileAuthenticationError({
      code: "invalid_payload",
      message: "Mobile authentication payload is missing",
      bridgeVersion: "unknown",
    });
  }

  if (!normalizedMessage.startsWith("{")) {
    const legacyUserId = normalizeRequiredString(normalizedMessage, MAX_FIREBASE_USER_ID_LENGTH);
    if (!legacyUserId) {
      throw new MobileAuthenticationError({
        code: "invalid_payload",
        message: "Legacy mobile authentication payload is invalid",
        bridgeVersion: "legacy",
      });
    }
    return {
      kind: "legacy",
      userId: legacyUserId,
    };
  }

  let parsedPayload: Record<string, unknown>;
  try {
    parsedPayload = JSON.parse(normalizedMessage) as Record<string, unknown>;
  } catch {
    throw new MobileAuthenticationError({
      code: "invalid_payload",
      message: "Mobile authentication payload is not valid JSON",
      bridgeVersion: "unknown",
    });
  }

  if (parsedPayload.bridgeVersion !== MOBILE_AUTHENTICATION_BRIDGE_VERSION) {
    throw new MobileAuthenticationError({
      code: "unsupported_bridge_version",
      message: "Mobile authentication bridge version is unsupported",
      bridgeVersion: String(parsedPayload.bridgeVersion ?? "unknown"),
    });
  }

  const userId = normalizeRequiredString(parsedPayload.userId, MAX_FIREBASE_USER_ID_LENGTH);
  const firebaseIdToken = normalizeRequiredString(parsedPayload.firebaseIdToken, MAX_FIREBASE_ID_TOKEN_LENGTH);
  const webCustomToken = normalizeRequiredString(parsedPayload.webCustomToken, MAX_WEB_CUSTOM_TOKEN_LENGTH);
  const nativeAppVersion = normalizeOptionalVersionField(parsedPayload.nativeAppVersion);
  const nativeBuildNumber = normalizeOptionalVersionField(parsedPayload.nativeBuildNumber);

  if (!userId || !firebaseIdToken) {
    throw new MobileAuthenticationError({
      code: "invalid_payload",
      message: "Secure mobile authentication payload is incomplete",
      bridgeVersion: String(MOBILE_AUTHENTICATION_BRIDGE_VERSION),
      userId: userId ?? undefined,
      nativeAppVersion,
      nativeBuildNumber,
    });
  }

  return {
    kind: "secure",
    payload: {
      bridgeVersion: MOBILE_AUTHENTICATION_BRIDGE_VERSION,
      userId,
      firebaseIdToken,
      ...(webCustomToken ? { webCustomToken } : {}),
      nativeAppVersion,
      nativeBuildNumber,
    },
  };
}

async function authenticateParsedMobileMessage(
  parsedMessage: ParsedMobileAuthenticationMessage,
  requestGeneration: number,
  noteSessionRejected?: () => void
): Promise<MobileAuthenticationResult> {
  const userId = parsedMessage.kind === "secure" ? parsedMessage.payload.userId : parsedMessage.userId;
  const isRequestCurrent = () => requestGeneration === authenticationRequestGeneration;
  const initialSessionResult = await queueFirebaseAuthenticationMutation(async () => {
    if (!isRequestCurrent()) return "superseded" as const;
    if (auth.currentUser?.uid === userId) return "already_signed_in" as const;
    if (!auth.currentUser) return "continue" as const;

    const previousWebViewUserId = auth.currentUser.uid;
    try {
      markFirebaseSessionEndAsExpected();
      await signOut(auth);
      return isRequestCurrent() ? ("continue" as const) : ("superseded" as const);
    } catch {
      markFirebaseSessionAsExpected(previousWebViewUserId);
      if (!isRequestCurrent()) return "superseded" as const;
      throw new MobileAuthenticationError({
        code: "firebase_sign_out_failed",
        message: "The previous WebView Firebase session could not be cleared",
        bridgeVersion: parsedMessage.kind === "secure" ? String(MOBILE_AUTHENTICATION_BRIDGE_VERSION) : "legacy",
        userId,
      });
    }
  });

  if (initialSessionResult === "superseded") {
    return createSupersededResult(parsedMessage, userId);
  }

  if (initialSessionResult === "already_signed_in") {
    return {
      status: "already_signed_in",
      bridgeVersion: parsedMessage.kind === "secure" ? MOBILE_AUTHENTICATION_BRIDGE_VERSION : ("legacy" as const),
      userId,
      nativeAppVersion: parsedMessage.kind === "secure" ? parsedMessage.payload.nativeAppVersion : undefined,
      nativeBuildNumber: parsedMessage.kind === "secure" ? parsedMessage.payload.nativeBuildNumber : undefined,
    };
  }

  if (parsedMessage.kind === "legacy") {
    return {
      status: "legacy_bridge_rejected",
      bridgeVersion: "legacy",
      userId,
    };
  }

  const {
    firebaseIdToken,
    nativeAppVersion,
    nativeBuildNumber,
    webCustomToken: nativeExchangedCustomToken,
  } = parsedMessage.payload;

  let customToken = nativeExchangedCustomToken ?? null;

  if (!customToken) {
    try {
      customToken = await exchangeFirebaseIdTokenForWebCustomToken({
        userId,
        firebaseIdToken,
        nativeAppVersion,
        nativeBuildNumber,
        noteSessionRejected,
      });
    } catch (tokenExchangeError) {
      if (!isRequestCurrent()) {
        return createSupersededResult(parsedMessage, userId);
      }
      throw tokenExchangeError;
    }

    if (!isRequestCurrent()) {
      return createSupersededResult(parsedMessage, userId);
    }
  }

  const signInCurrentRequestWithCustomToken = (currentCustomToken: string): Promise<boolean> =>
    queueFirebaseAuthenticationMutation(async () => {
      if (!isRequestCurrent()) return false;
      await signInWithCustomToken(auth, currentCustomToken);
      return isRequestCurrent();
    });

  let initialFirebaseErrorCode: MobileAuthenticationFirebaseErrorCode | undefined;
  try {
    const didSignInCurrentRequest = await signInCurrentRequestWithCustomToken(customToken);
    if (!didSignInCurrentRequest) {
      return createSupersededResult(parsedMessage, userId);
    }
  } catch (error) {
    if (!isRequestCurrent()) {
      return createSupersededResult(parsedMessage, userId);
    }

    const firebaseErrorCode = getFirebaseAuthenticationErrorCode(error);
    if (!isRetryableFirebaseAuthenticationErrorCode(firebaseErrorCode)) {
      throw new MobileAuthenticationError({
        code: "firebase_sign_in_failed",
        message: "Firebase rejected the exchanged mobile authentication token",
        bridgeVersion: String(MOBILE_AUTHENTICATION_BRIDGE_VERSION),
        userId,
        nativeAppVersion,
        nativeBuildNumber,
        firebaseErrorCode,
        firebaseRetryAttempted: false,
      });
    }

    initialFirebaseErrorCode = firebaseErrorCode;
    try {
      customToken = await exchangeFirebaseIdTokenForWebCustomToken({
        userId,
        firebaseIdToken,
        nativeAppVersion,
        nativeBuildNumber,
        noteSessionRejected,
      });
    } catch (retryExchangeError) {
      if (!isRequestCurrent()) {
        return createSupersededResult(parsedMessage, userId);
      }
      const retryExchangeAuthenticationError =
        retryExchangeError instanceof MobileAuthenticationError ? retryExchangeError : null;
      throw new MobileAuthenticationError({
        code: retryExchangeAuthenticationError?.code === "session_reauthentication_required"
          ? "session_reauthentication_required" : "firebase_sign_in_failed",
        message: "Firebase sign-in recovery could not obtain a fresh authentication token",
        bridgeVersion: String(MOBILE_AUTHENTICATION_BRIDGE_VERSION),
        userId,
        nativeAppVersion,
        nativeBuildNumber,
        responseStatus: retryExchangeAuthenticationError?.responseStatus,
        firebaseErrorCode,
        initialFirebaseErrorCode,
        firebaseRetryAttempted: true,
        firebaseRetryFailureStage: "token_exchange",
      });
    }

    if (!isRequestCurrent()) {
      return createSupersededResult(parsedMessage, userId);
    }

    try {
      const didRetryCurrentRequest = await signInCurrentRequestWithCustomToken(customToken);
      if (!didRetryCurrentRequest) {
        return createSupersededResult(parsedMessage, userId);
      }
    } catch (retrySignInError) {
      if (!isRequestCurrent()) {
        return createSupersededResult(parsedMessage, userId);
      }
      const finalFirebaseErrorCode = getFirebaseAuthenticationErrorCode(retrySignInError);
      throw new MobileAuthenticationError({
        code: "firebase_sign_in_failed",
        message: "Firebase rejected the refreshed mobile authentication token",
        bridgeVersion: String(MOBILE_AUTHENTICATION_BRIDGE_VERSION),
        userId,
        nativeAppVersion,
        nativeBuildNumber,
        firebaseErrorCode: finalFirebaseErrorCode,
        initialFirebaseErrorCode,
        firebaseRetryAttempted: true,
        firebaseRetryFailureStage: "firebase_sign_in",
      });
    }
  }

  return {
    status: "signed_in",
    bridgeVersion: MOBILE_AUTHENTICATION_BRIDGE_VERSION,
    userId,
    nativeAppVersion,
    nativeBuildNumber,
    ...(initialFirebaseErrorCode
      ? {
          firebaseRetryAttempted: true,
          initialFirebaseErrorCode,
        }
      : {}),
  };
}

export async function authenticateWebViewFromMobileMessage(
  rawMessage: unknown,
  requestNativeReauthentication?: (userId: string, sessionIdentifier: string) => void,
  noteRejectedNativeSession?: (sessionIdentifier: string) => void
): Promise<MobileAuthenticationResult> {
  const parsedMessage = parseMobileAuthenticationMessage(rawMessage);
  const authenticationRequestKey =
    parsedMessage.kind === "secure"
      ? [
          parsedMessage.payload.bridgeVersion,
          parsedMessage.payload.userId,
          parsedMessage.payload.nativeAppVersion ?? "unknown",
          parsedMessage.payload.nativeBuildNumber ?? "unknown",
          getSecureMobileAuthenticationSessionIdentifier(parsedMessage.payload),
        ].join(":")
      : `legacy:${parsedMessage.userId}`;
  const existingAuthenticationRequest = authenticationRequestsInFlight.get(authenticationRequestKey);
  if (existingAuthenticationRequest?.generation === authenticationRequestGeneration) return existingAuthenticationRequest.promise;

  authenticationRequestGeneration += 1;
  const requestGeneration = authenticationRequestGeneration;
  const noteSessionRejected = parsedMessage.kind === "secure"
    ? () => noteRejectedNativeSession?.(getSecureMobileAuthenticationSessionIdentifier(parsedMessage.payload))
    : undefined;
  const authenticationRequest = authenticateParsedMobileMessage(parsedMessage, requestGeneration, noteSessionRejected).catch(async (error) => {
    const userId = parsedMessage.kind === "secure" ? parsedMessage.payload.userId : parsedMessage.userId;
    if (requestGeneration !== authenticationRequestGeneration) return createSupersededResult(parsedMessage, userId);
    if (!(error instanceof MobileAuthenticationError) || error.code !== "session_reauthentication_required") throw error;

    return queueFirebaseAuthenticationMutation(async () => {
      if (requestGeneration !== authenticationRequestGeneration) return createSupersededResult(parsedMessage, userId);
      // Post before awaiting Firebase: a newer native account must never be
      // signed out by the delayed completion of an older exchange/sign-out.
      if (parsedMessage.kind === "secure") {
        requestNativeReauthentication?.(userId, getSecureMobileAuthenticationSessionIdentifier(parsedMessage.payload));
      }
      const previousUserId = auth.currentUser?.uid;
      markFirebaseSessionEndAsExpected();
      try {
        await signOut(auth);
      } catch {
        if (previousUserId) markFirebaseSessionAsExpected(previousUserId);
        throw new MobileAuthenticationError({
          code: "firebase_sign_out_failed",
          message: "The rejected WebView Firebase session could not be cleared",
          bridgeVersion: String(MOBILE_AUTHENTICATION_BRIDGE_VERSION),
          userId,
        });
      }
      if (requestGeneration !== authenticationRequestGeneration) return createSupersededResult(parsedMessage, userId);
      return { ...createSupersededResult(parsedMessage, userId), status: "reauthentication_required" as const };
    });
  });
  authenticationRequestsInFlight.set(authenticationRequestKey, { generation: requestGeneration, promise: authenticationRequest });

  try {
    return await authenticationRequest;
  } finally {
    if (authenticationRequestsInFlight.get(authenticationRequestKey)?.promise === authenticationRequest) {
      authenticationRequestsInFlight.delete(authenticationRequestKey);
    }
  }
}

export function signOutWebViewAfterMobileAuthenticationEnded(): Promise<void> {
  if (signOutRequestInFlight) return signOutRequestInFlight;

  authenticationRequestGeneration += 1;
  const signOutRequest = queueFirebaseAuthenticationMutation(async () => {
    const webViewUserId = auth.currentUser?.uid;
    try {
      markFirebaseSessionEndAsExpected();
      await signOut(auth);
    } catch {
      if (webViewUserId) {
        markFirebaseSessionAsExpected(webViewUserId);
      }
      throw new MobileAuthenticationError({
        code: "firebase_sign_out_failed",
        message: "The WebView Firebase session could not be signed out",
        bridgeVersion: String(MOBILE_AUTHENTICATION_BRIDGE_VERSION),
      });
    }
  });
  signOutRequestInFlight = signOutRequest;
  const clearSignOutRequest = () => {
    if (signOutRequestInFlight === signOutRequest) {
      signOutRequestInFlight = null;
    }
  };
  void signOutRequest.then(clearSignOutRequest, clearSignOutRequest);
  return signOutRequest;
}
